/**
 * Vendor brand marks — identity without semantic drift.
 *
 * Three rules, enforced here rather than in prose:
 *
 *  1. **Bound by stdlib module id.** A mark is looked up by the id a stdlib
 *     type stamps (`cascade widget.brand: "arch.backend.postgres"`), never
 *     inferred from a module's name, label or description. A model that never
 *     names a vendor never grows a vendor mark.
 *  2. **Vendor colour is contained.** The mark paints inside a neutral plate,
 *     so brand colour can't compete with the kind/aspect palette that carries
 *     meaning on the board.
 *  3. **It costs layout, and degrades.** The plate reserves {@link BRAND_RAIL}
 *     px out of the card's label rail — the same width the solver's name fit
 *     measures against. When what would be left is under
 *     {@link BRAND_MIN_LABEL_RAIL}, the MARK is dropped, not the name: the
 *     §10.2 escalation (shrink → grow → diagnose) never ends in a failed render
 *     because of decoration.
 *
 * The mark is `aria-hidden` and carries no interaction state — the module's
 * name is the accessible identity; the mark is a recognition aid on top of it.
 *
 * Artwork provenance lives in `brand-marks.data.ts` (generated from Simple
 * Icons, CC0-1.0, per-mark trademark review). A vendor with no CC0-sourced
 * mark ships none — see `OMITTED_BRAND_MARKS`.
 */
import { box, glyph, type Node } from "../model.js";
import { alpha, type Tokens } from "../tokens.js";
import { BRAND_MARK_DATA } from "./brand-marks.data.js";

export interface BrandMark {
  /** stdlib module id the mark binds to (`widget.brand`). */
  readonly id: string;
  /** Vendor/product name the mark stands for. */
  readonly title: string;
  /** Simple Icons slug the artwork came from. */
  readonly slug: string;
  /** Vendor colour, drawn inside the neutral plate only. */
  readonly hex: string;
  /** Trademark review that cleared this mark for shipping. */
  readonly review: string;
  /** Path data against a 0 0 24 24 viewBox. */
  readonly path: string;
}

export interface OmittedBrandMark {
  readonly id: string;
  readonly title: string;
  readonly reason: string;
}

/** Plate edge (px) — the drawn square the mark sits in. */
export const BRAND_PLATE_SIZE = 32;

/** Label rail (px) the plate reserves out of the card's inner width: the plate
 *  plus the gap that separates it from the name. */
export const BRAND_RAIL = 48;

/** Label rail (px) the NAME must keep. Below this the mark degrades away
 *  rather than squeezing the name that carries the model's meaning. */
export const BRAND_MIN_LABEL_RAIL = 64;

const BY_ID = new Map(BRAND_MARK_DATA.map((m) => [m.id, m]));

/** The mark bound to a stdlib module id, or undefined for an unknown id (an
 *  id we ship no verified artwork for renders as a plain card). */
export function brandMark(id: string | undefined): BrandMark | undefined {
  return id ? BY_ID.get(id) : undefined;
}

export interface BrandRail {
  /** The mark that survived the rail check; undefined when it degraded away. */
  readonly mark?: BrandMark;
  /** Width (px) the mark takes out of the card's inner width — 0 with no mark. */
  readonly rail: number;
}

const NO_RAIL: BrandRail = { rail: 0 };

/**
 * Resolve the mark for `id` against the card's inner width, degrading (never
 * failing) when the rail that would be left for the name is too short. Both the
 * composer and the solver's node sizing call this, so live and export reserve
 * — or drop — identically.
 */
export function brandRail(id: string | undefined, innerWidth: number): BrandRail {
  const mark = brandMark(id);
  if (!mark) return NO_RAIL;
  if (innerWidth - BRAND_RAIL < BRAND_MIN_LABEL_RAIL) return NO_RAIL;
  return { mark, rail: BRAND_RAIL };
}

/** Silhouette a SELF-SIZED shape (the database cylinder) needs before it hosts
 *  a corner plate: wide enough to sit beside the badge, and tall enough that
 *  the plate doesn't land on the caption — the squat `cache` silhouette is
 *  exactly the shape that fails the height test. */
export const BRAND_MIN_SHAPE_WIDTH = 96;
export const BRAND_MIN_SHAPE_HEIGHT = 120;

/**
 * The mark for a shape that has no label rail to reserve — the cylinder, whose
 * plate rides a corner overlay the way its badge does. Same degradation shape
 * as {@link brandRail}: too small a silhouette drops the mark, never the shape.
 */
export function brandCornerMark(
  id: string | undefined,
  dims: { readonly width: number; readonly height: number },
): BrandMark | undefined {
  return dims.width >= BRAND_MIN_SHAPE_WIDTH && dims.height >= BRAND_MIN_SHAPE_HEIGHT
    ? brandMark(id)
    : undefined;
}

/**
 * The neutral plate: a light, near-opaque square with the vendor mark drawn in
 * its own colour inside it. `aria-hidden` — the card's name is the accessible
 * identity, and a decorative mark must not be announced twice.
 */
export function brandPlateNode(mark: BrandMark, t: Tokens): Node {
  const pad = Math.round(BRAND_PLATE_SIZE * 0.19);
  return box({
    direction: "row",
    align: "center",
    justify: "center",
    width: BRAND_PLATE_SIZE,
    height: BRAND_PLATE_SIZE,
    padding: pad,
    ariaHidden: true,
    style: {
      // Neutral, theme-independent plate: the vendor colour is contained by it
      // in every theme and in export, where no stylesheet resolves.
      fill: "#ffffff",
      stroke: alpha(t.fg, 0.14),
      strokeWidth: t.borderThin,
      radius: t.radiusSm,
    },
    children: [
      glyph({
        viewW: 24,
        viewH: 24,
        width: BRAND_PLATE_SIZE - 2 * pad,
        height: BRAND_PLATE_SIZE - 2 * pad,
        color: mark.hex,
        body: `<path fill="${mark.hex}" d="${mark.path}"/>`,
      }),
    ],
  });
}

export { BRAND_MARK_COLLECTION, BRAND_MARK_DATA, OMITTED_BRAND_MARKS } from "./brand-marks.data.js";
